import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  TextInput,
  NumberInput,
  ComboBox,
  Button,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  InlineNotification,
  Stack,
} from "@carbon/react";
import { FormattedMessage, useIntl } from "react-intl";
import { InventoryBoardAPI, InventoryManagementAPI } from "./InventoryService";
import InventoryItemForm from "./InventoryItemForm";
import { parseGs1, gtinMatches } from "./gs1";
import { includesComboBoxText } from "../analyzers/comboBoxSearch";
import { toLocalIsoDate } from "../utils/Utils";
import { labNow } from "../utils/labClock";
import "./InventoryItemsBoard.css";

const ReceiveDelivery = () => {
  const intl = useIntl();
  const scanRef = useRef(null);
  const nextLineKey = useRef(0);

  const [rows, setRows] = useState([]);
  const [lines, setLines] = useState([]);
  const [scan, setScan] = useState("");
  const [lastScan, setLastScan] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unknownUpc, setUnknownUpc] = useState(null);
  const [definingUpc, setDefiningUpc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await InventoryBoardAPI.get());
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const options = rows.map((row) => ({
    id: row.itemId,
    text: row.code ? `${row.name} (${row.code})` : row.name,
    row,
  }));
  const selected = options.find((option) => option.id === itemId) || null;
  const lotless = selected?.row.trackLots === false;

  const today = toLocalIsoDate(labNow());
  const expiredAlready = !lotless && expirationDate && expirationDate < today;

  const clearLine = () => {
    setItemId(null);
    setLotNumber("");
    setExpirationDate("");
    setQuantity(1);
    setLastScan(null);
  };

  const focusScan = () => {
    if (scanRef.current) scanRef.current.focus();
  };

  const handleScan = (value) => {
    const parsed = parseGs1(value);
    setScan("");
    if (!parsed.raw) return;
    setLastScan(parsed);

    // A scan overwrites the previous scan's batch and expiry, even with blanks;
    // values typed with no scan behind them are kept.
    setLotNumber(parsed.fields.lotNumber || (lastScan ? "" : lotNumber));
    setExpirationDate(
      parsed.fields.expirationDate || (lastScan ? "" : expirationDate),
    );

    if (parsed.fields.gtin) {
      const match = options.find((option) =>
        gtinMatches(option.row.upc, parsed.fields.gtin),
      );
      if (match) {
        setItemId(match.id);
        setUnknownUpc(null);
      } else {
        setUnknownUpc(parsed.fields.gtin);
      }
    }
  };

  const addLine = () => {
    if (itemId == null) {
      setError(intl.formatMessage({ id: "inventory.receive.error.noItem" }));
      return;
    }
    if (!(quantity > 0)) {
      setError(intl.formatMessage({ id: "inventory.receive.error.quantity" }));
      return;
    }
    setError(null);
    setLines((previous) => [
      ...previous,
      {
        key: `line-${nextLineKey.current++}`,
        itemId,
        itemName: selected.text,
        units: selected.row.units,
        lotNumber: lotless ? "" : lotNumber,
        expirationDate: lotless ? "" : expirationDate,
        quantity,
        rawScan: lastScan?.raw || null,
        unparsed: lastScan?.unparsed?.length
          ? lastScan.unparsed.join(" ")
          : null,
      },
    ]);
    clearLine();
    focusScan();
  };

  const removeLine = (key) =>
    setLines((previous) => previous.filter((line) => line.key !== key));

  const commit = async () => {
    if (lines.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await InventoryManagementAPI.receiveBatch(
        lines.map((line) => ({
          inventoryItem: { id: line.itemId },
          lotNumber: line.lotNumber ? line.lotNumber : null,
          initialQuantity: line.quantity,
          currentQuantity: line.quantity,
          expirationDate: line.expirationDate
            ? new Date(line.expirationDate).toISOString()
            : null,
        })),
      );
      setDone(lines.length);
      setLines([]);
      clearLine();
      focusScan();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="receive-delivery">
      <p className="board-purpose">
        <FormattedMessage id="inventory.delivery.purpose" />
      </p>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={intl.formatMessage({ id: "notification.error" })}
          subtitle={error}
        />
      )}

      {done != null && (
        <InlineNotification
          kind="success"
          lowContrast
          onClose={() => setDone(null)}
          title={intl.formatMessage({ id: "notification.success" })}
          subtitle={intl.formatMessage(
            { id: "inventory.delivery.received" },
            { count: done },
          )}
        />
      )}

      <Stack gap={5}>
        <TextInput
          id="delivery-scan"
          ref={scanRef}
          labelText={<FormattedMessage id="inventory.delivery.scan" />}
          helperText={intl.formatMessage({
            id: "inventory.delivery.scan.help",
          })}
          value={scan}
          onChange={(event) => setScan(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            // A scanner ends each code with Enter.
            event.preventDefault();
            handleScan(scan);
          }}
        />

        {lastScan && !lastScan.recognised && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title={intl.formatMessage({ id: "inventory.delivery.scan.unread" })}
            subtitle={lastScan.raw}
          />
        )}

        {unknownUpc && (
          <div className="delivery-unknown">
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title={intl.formatMessage({
                id: "inventory.receive.scan.unknown",
              })}
              subtitle={unknownUpc}
            />
            <Button
              kind="tertiary"
              size="sm"
              onClick={() => setDefiningUpc(unknownUpc)}
            >
              <FormattedMessage id="inventory.item.new" />
            </Button>
          </div>
        )}

        <div className="delivery-line">
          <ComboBox
            id="delivery-item"
            titleText={<FormattedMessage id="catalog.item.name" />}
            placeholder={intl.formatMessage({
              id: "inventory.search.placeholder",
            })}
            items={options}
            selectedItem={selected}
            itemToString={(option) => (option ? option.text : "")}
            shouldFilterItem={includesComboBoxText}
            onChange={({ selectedItem }) =>
              setItemId(selectedItem ? selectedItem.id : null)
            }
          />
          {!lotless && (
            <>
              <TextInput
                id="delivery-lot"
                labelText={<FormattedMessage id="lot.number" />}
                value={lotNumber}
                onChange={(event) => setLotNumber(event.target.value)}
              />
              <TextInput
                id="delivery-expiry"
                type="date"
                labelText={<FormattedMessage id="lot.expirationDate" />}
                value={expirationDate}
                onChange={(event) => setExpirationDate(event.target.value)}
              />
            </>
          )}
          <NumberInput
            id="delivery-quantity"
            label={<FormattedMessage id="lot.currentQuantity" />}
            min={1}
            step={1}
            value={quantity}
            onChange={(event, { value }) => setQuantity(Number(value))}
          />
          <Button kind="tertiary" size="md" onClick={addLine}>
            <FormattedMessage id="inventory.delivery.add" />
          </Button>
        </div>

        {expiredAlready && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title={intl.formatMessage({ id: "inventory.delivery.expired" })}
            subtitle={intl.formatMessage({
              id: "inventory.delivery.expired.help",
            })}
          />
        )}

        {lines.length > 0 && (
          <>
            <Table size="sm">
              <TableHead>
                <TableRow>
                  <TableHeader>
                    <FormattedMessage id="catalog.item.name" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="lot.number" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="lot.expirationDate" />
                  </TableHeader>
                  <TableHeader>
                    <FormattedMessage id="lot.currentQuantity" />
                  </TableHeader>
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.key}>
                    <TableCell>
                      {line.itemName}
                      {line.unparsed && (
                        <>
                          <Tag size="sm" type="warm-gray">
                            <FormattedMessage id="inventory.delivery.partialScan" />
                          </Tag>
                          <div className="board-muted">{line.rawScan}</div>
                        </>
                      )}
                    </TableCell>
                    <TableCell>{line.lotNumber || "—"}</TableCell>
                    <TableCell>{line.expirationDate || "—"}</TableCell>
                    <TableCell>
                      {line.quantity} {line.units}
                    </TableCell>
                    <TableCell>
                      <Button
                        kind="ghost"
                        size="sm"
                        onClick={() => removeLine(line.key)}
                      >
                        <FormattedMessage id="inventory.delivery.remove" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="delivery-actions">
              <Button kind="primary" disabled={saving} onClick={commit}>
                <FormattedMessage
                  id="inventory.delivery.confirm"
                  values={{ count: lines.length }}
                />
              </Button>
              <Button kind="ghost" onClick={() => setLines([])}>
                <FormattedMessage id="inventory.delivery.discard" />
              </Button>
            </div>
          </>
        )}
      </Stack>

      {definingUpc && (
        <InventoryItemForm
          open
          item={null}
          initialUpc={definingUpc}
          onClose={() => setDefiningUpc(null)}
          onSave={() => {
            setDefiningUpc(null);
            setUnknownUpc(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ReceiveDelivery;
